import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Loader2, X, Target, Briefcase, Building2, Award, GraduationCap, Users, Brain } from 'lucide-react';
import api from '../../services/api';

// Shape persistido: array de { urn, name } por facet. Backend espera URNs
// (ex: "urn:li:industry:1410"), nome é só pra display na UI.
export interface LinkedInTargetingItem {
  urn: string;
  name: string;
  facet?: string;
}

export type LinkedInTargeting = {
  industries?: LinkedInTargetingItem[];
  jobTitles?: LinkedInTargetingItem[];
  jobFunctions?: LinkedInTargetingItem[];
  seniorities?: LinkedInTargetingItem[];
  skills?: LinkedInTargetingItem[];
  staffCountRanges?: LinkedInTargetingItem[];
  companies?: LinkedInTargetingItem[];
  degrees?: LinkedInTargetingItem[];
  fieldsOfStudy?: LinkedInTargetingItem[];
  interests?: LinkedInTargetingItem[];
};

interface Props {
  value: LinkedInTargeting;
  onChange: (next: LinkedInTargeting) => void;
}

// Lista de facets renderizados — cada um vira uma seção com autocomplete independente.
// Facets "typeahead=false" (seniorities, staffCountRanges, jobFunctions, degrees) carregam
// a lista fixa completa do LinkedIn ao expandir.
const FACETS: Array<{
  key: keyof LinkedInTargeting;
  label: string;
  placeholder: string;
  icon: React.ComponentType<{ className?: string }>;
  typeahead: boolean;
}> = [
  { key: 'industries', label: 'Indústrias', placeholder: 'Ex: Tecnologia, Software, Marketing...', icon: Building2, typeahead: true },
  { key: 'jobTitles', label: 'Cargos (Job Titles)', placeholder: 'Ex: CEO, Marketing Manager, CTO...', icon: Briefcase, typeahead: true },
  { key: 'jobFunctions', label: 'Funções', placeholder: 'Abrir lista de funções...', icon: Briefcase, typeahead: false },
  { key: 'seniorities', label: 'Senioridade', placeholder: 'Abrir lista de senioridades...', icon: Award, typeahead: false },
  { key: 'skills', label: 'Habilidades', placeholder: 'Ex: JavaScript, SEO, Copywriting...', icon: Brain, typeahead: true },
  { key: 'staffCountRanges', label: 'Tamanho da Empresa', placeholder: 'Abrir faixas de tamanho...', icon: Users, typeahead: false },
  { key: 'companies', label: 'Empresas Específicas', placeholder: 'Ex: Google, Microsoft, Nubank...', icon: Building2, typeahead: true },
  { key: 'degrees', label: 'Graus Acadêmicos', placeholder: 'Abrir graus...', icon: GraduationCap, typeahead: false },
  { key: 'fieldsOfStudy', label: 'Áreas de Estudo', placeholder: 'Ex: Computer Science, Marketing...', icon: GraduationCap, typeahead: true },
  { key: 'interests', label: 'Interesses', placeholder: 'Ex: Startups, Design, Finanças...', icon: Target, typeahead: true },
];

const LinkedInTargetingInput: React.FC<Props> = ({ value, onChange }) => {
  const { t } = useTranslation();

  const updateFacet = (key: keyof LinkedInTargeting, items: LinkedInTargetingItem[]) => {
    onChange({ ...value, [key]: items });
  };

  return (
    <div className="space-y-4">
      <div className="p-3 bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800 rounded-lg text-sm text-sky-800 dark:text-sky-300">
        <p className="font-medium mb-1">💼 LinkedIn Targeting (B2B)</p>
        <p className="text-xs">
          {t('campaigns.linkedinTargeting.hint', 'Combine filtros pra atingir o público profissional ideal. Todos valores vêm do catálogo oficial da LinkedIn — não é texto livre.')}
        </p>
      </div>

      {FACETS.map(facet => (
        <FacetSection
          key={String(facet.key)}
          facetKey={String(facet.key)}
          label={facet.label}
          placeholder={facet.placeholder}
          Icon={facet.icon}
          typeahead={facet.typeahead}
          selected={(value[facet.key] as LinkedInTargetingItem[]) || []}
          onChange={(items) => updateFacet(facet.key, items)}
        />
      ))}
    </div>
  );
};

// Sub-componente de seção (1 facet) — autocomplete-com-chips
const FacetSection: React.FC<{
  facetKey: string;
  label: string;
  placeholder: string;
  Icon: React.ComponentType<{ className?: string }>;
  typeahead: boolean;
  selected: LinkedInTargetingItem[];
  onChange: (items: LinkedInTargetingItem[]) => void;
}> = ({ facetKey, label, placeholder, Icon, typeahead, selected, onChange }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<LinkedInTargetingItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [fixedListLoaded, setFixedListLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Para typeahead=false, carregar a lista fixa ao abrir o dropdown
  const loadFixedList = async () => {
    if (fixedListLoaded || typeahead) return;
    setSearching(true);
    try {
      const r = await api.get(`/campaigns/targeting/linkedin/${facetKey}/search`);
      if (r.data?.success) setResults(r.data.data || []);
      setFixedListLoaded(true);
    } catch { /* silent */ } finally { setSearching(false); }
  };

  useEffect(() => {
    if (!typeahead) return;
    if (query.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.get(`/campaigns/targeting/linkedin/${facetKey}/search`, { params: { q: query } });
        if (r.data?.success) {
          const existing = new Set(selected.map(s => s.urn));
          setResults((r.data.data || []).filter((x: LinkedInTargetingItem) => !existing.has(x.urn)));
        }
      } catch { setResults([]); } finally { setSearching(false); }
    }, 400);
    return () => clearTimeout(t);
  }, [query, facetKey, typeahead, selected]);

  // Click fora fecha
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const add = (item: LinkedInTargetingItem) => {
    if (selected.some(s => s.urn === item.urn)) return;
    onChange([...selected, item]);
    setQuery('');
    if (typeahead) setResults([]);
  };

  const remove = (urn: string) => {
    onChange(selected.filter(s => s.urn !== urn));
  };

  // Para facet com lista fixa, filtra os já selecionados do dropdown
  const displayResults = typeahead
    ? results
    : results.filter(r => !selected.some(s => s.urn === r.urn));

  return (
    <div ref={containerRef} className="relative">
      <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
        <Icon className="w-4 h-4 text-sky-500" />
        {label}
        {selected.length > 0 && (
          <span className="ml-1 text-xs text-gray-400">({selected.length})</span>
        )}
      </label>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={query}
          placeholder={placeholder}
          readOnly={!typeahead}
          onChange={(e) => { setQuery(e.target.value); setIsOpen(true); }}
          onFocus={() => { setIsOpen(true); if (!typeahead) loadFixedList(); }}
          className="w-full pl-9 pr-9 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
        />
        {searching && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />
        )}
      </div>

      {isOpen && displayResults.length > 0 && (
        <div className="absolute z-20 left-0 right-0 mt-1 max-h-56 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl">
          {displayResults.map(item => (
            <button
              key={item.urn}
              type="button"
              onClick={() => add(item)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 border-b border-gray-100 dark:border-gray-700/50 last:border-0"
            >
              <Icon className="w-3.5 h-3.5 text-sky-500 flex-shrink-0" />
              <span className="text-gray-900 dark:text-white truncate">{item.name}</span>
            </button>
          ))}
        </div>
      )}

      {isOpen && typeahead && query.length >= 2 && !searching && displayResults.length === 0 && (
        <div className="absolute z-20 left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl px-3 py-3 text-sm text-gray-500 dark:text-gray-400">
          {t('campaigns.linkedinTargeting.noResults', 'Nenhum resultado')}
        </div>
      )}

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {selected.map(item => (
            <span
              key={item.urn}
              className="inline-flex items-center gap-1 px-2 py-1 bg-sky-50 dark:bg-sky-900/30 rounded-full text-xs text-sky-700 dark:text-sky-300"
            >
              {item.name}
              <button type="button" onClick={() => remove(item.urn)} className="hover:text-red-500">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default LinkedInTargetingInput;
